export const COLOR_BINS = 32
export const RGB_BINS = COLOR_BINS ** 3
export const BYTES_PER_FRAME = RGB_BINS / 8
export const HUE_BINS = 16

export type ColorIndex = {
    bytes: Uint32Array
    length: number
}

export function colorIndexFromUint8(...bytes: Uint8Array[]): ColorIndex {
    const totalLength = bytes.reduce((acc, arr) => acc + arr.byteLength, 0)
    const result = new Uint8Array(totalLength)

    let offset = 0
    for (const arr of bytes) {
        result.set(arr, offset)
        offset += arr.byteLength
    }

    return buildColorIndex(new Uint32Array(result.buffer, result.byteOffset, result.byteLength / 4))
}

export function buildColorIndex(bytes: Uint32Array): ColorIndex {
    return {
        bytes,
        length: bytes.length / (4096 / 4),
    }
}

export const colorKeyToRGB = (color: number) => {
    const red = (color >> 10) << 3
    const green = ((color >> 5) & 31) << 3
    const blue = (color & 31) << 3

    return [red, green, blue]
}

export function rgbIndexToRgbV2(cumulative: Uint32Array) {
    const nums: number[][] = []
    for (let i = 0; i < cumulative.length; i++) {
        let word = cumulative[i]
        while (word) {
            const bit = 31 - Math.clz32(word)

            const color = (i << 5) + bit

            const red = (color >> 10) << 3
            const green = ((color >> 5) & 31) << 3
            const blue = (color & 31) << 3

            nums.push([red, green, blue])

            // This was already correct for clearing the *lowest* bit
            word ^= 1 << bit
        }
    }
    return nums
}

export interface FrameIndex {
    rgb: [number, number, number][]
    hues: number[]
}

export function hcb2rgb(h: number, c: number, brightness: number) {
    const { PI, sin, cos, max, min } = Math

    const hue = -PI / 4 - 2 * PI * h
    const tilt = sin(PI / 4)
    const red = min(max(brightness + c * cos(hue + PI / 4) * tilt, 0), 1) * 255
    const green = min(max(brightness + c * cos(PI / 4 - hue) * tilt, 0), 1) * 255
    const blue = min(max(brightness - c * cos(hue) * tilt, 0), 1) * 255
    return [red, green, blue]
}

const PI = Math.PI
const TAU = PI * 2
const { sqrt, atan2 } = Math

export function rgb2hcb(colorKey: number, r: number, g: number, b: number) {
    const brightness = (r + g + b) / 3
    const chroma = sqrt(
        (r - brightness) * (r - brightness) +
            (g - brightness) * (g - brightness) +
            (b - brightness) * (b - brightness)
    )
    const hue = (atan2(r - b, g - b) - PI / 4 + TAU) % TAU

    const val = [hue, chroma, brightness]
    return val
}
